<script lang="ts">
/**
 * Page navigation for paginated lists.
 *
 * Wraps Nuxt UI's `UPagination` with URL-based page navigation.
 * Renders nothing when the result set fits on a single page.
 *
 * @example
 * ```vue
 * <PaginationNav :pagination="items.pagination" />
 * ```
 */
export interface PaginationNavProps {
  /** Pagination metadata from the backend (current_page, total_pages, etc.). */
  pagination: PaginationMeta
}
</script>

<script setup lang="ts">
import type { PaginationMeta } from "@/types/models"

const props = defineProps<PaginationNavProps>()

function toPage(page: number) {
  const url = new URL(window.location.href)
  if (page <= 1) {
    url.searchParams.delete("page")
  } else {
    url.searchParams.set("page", String(page))
  }
  return url.pathname + url.search
}
</script>

<template>
  <div v-if="pagination.total_pages > 1" class="flex justify-center pt-4">
    <UPagination
      :page="pagination.current_page"
      :total="pagination.total_count"
      :items-per-page="pagination.per_page"
      :to="toPage"
      size="sm"
      color="neutral"
      variant="subtle"
      :show-controls="false"
    />
  </div>
</template>
